import { useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import type { ConsoleApi } from './api/consoleApi';
import { ConsoleApiProvider } from './app/ConsoleApiContext';
import { createConsoleRouter } from './app/routes';

/**
 * Composition root: query client + data provider + router.
 *
 * `api` is injected (not imported) so tests can hand a page any provider — the
 * fixture-backed pages and a future live-backed page render from the same code.
 */
export function App({ api, base = '/' }: { api: ConsoleApi; base?: string }) {
  const client = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 1,
            refetchOnWindowFocus: false,
            staleTime: 15_000,
          },
        },
      }),
    [],
  );
  const router = useMemo(() => createConsoleRouter(base), [base]);

  return (
    <QueryClientProvider client={client}>
      <ConsoleApiProvider api={api}>
        <RouterProvider router={router} />
      </ConsoleApiProvider>
    </QueryClientProvider>
  );
}
